import React from 'react';

/**
 * The "ⓘ" mark. Decorative on purpose: `aria-hidden`, `focusable="false"`, and it
 * paints in `currentColor` so whatever wraps it owns the colour and its states.
 *
 * Shared by `InfoTooltip` (which makes it a real button) and `Dropdown`'s options
 * (where the option itself is already the focusable element, so a second focusable
 * control inside it would be both invalid HTML — interactive content inside a
 * `<button>` — and a needless extra tab stop).
 *
 * ## § 295 — it IS `InfoCircle`, at 16
 *
 * This was a traced organic fill on a 15×16 box behind a `clipPath` — the shape
 * `OverlayIcon` carried, ported in § 23 with its ids fixed but its drawing intact. It
 * sat on neither of the set's stroke bands (§ 232) and was visibly a different family
 * from the info mark `Toast`, `Alert` and `Dialog` draw through `TONE_GLYPH`: every
 * field label's "ⓘ" said one thing and every info banner another. The geometry below
 * is `Icons.InfoCircle`'s, verbatim — the 20-grid `r=8` ring at 1.5, the stem, the
 * `r=0.9` dot — rendered in the 16px slot `InfoTooltip` already reserves. It is
 * restated here rather than composed because this component forwards a ref to the
 * `<svg>` and the plain icon functions do not; `coherencePass.test.tsx` pins the two
 * drawings equal so they cannot drift.
 *
 * The clipPath (and the `useId` that gave each instance a unique one) is gone with the
 * fill it existed to clip. Nothing referenced the ids.
 */
const InfoGlyph = React.forwardRef<
  SVGSVGElement,
  React.SVGProps<SVGSVGElement>
>(function InfoGlyph({ className = '', ...rest }, ref) {
  return (
    <svg
      {...rest}
      ref={ref}
      width="16"
      height="16"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10 9V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="10" cy="6.25" r="0.9" fill="currentColor" />
    </svg>
  );
});

export default InfoGlyph;
